import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import WaitlistModal from "../components/WaitlistModal";
import SovereignToolkit from "../components/SovereignToolkit";
import { TrendingUp, TrendingDown, Activity, ArrowRight, Radio } from "lucide-react";
import EigooMark from "../components/EigooMark";
import { BoardLink } from "../lib/boardLinks";

const LIQUIDITY_SIGNALS = [
    { sym: "BTC/USD", bias: "long", conf: 0.82, spread: 12, vol: "4.2B", move: 2.3 },
    { sym: "ETH/USD", bias: "long", conf: 0.74, spread: 8, vol: "1.8B", move: 1.1 },
    { sym: "SPY", bias: "flat", conf: 0.51, spread: 0.02, vol: "68M", move: -0.1 },
    { sym: "NVDA", bias: "long", conf: 0.69, spread: 0.05, vol: "142M", move: 3.7 },
    { sym: "TSLA", bias: "short", conf: 0.63, spread: 0.06, vol: "98M", move: -2.4 },
    { sym: "QQQ", bias: "long", conf: 0.58, spread: 0.03, vol: "42M", move: 0.8 },
];

export default function TradeHome() {
    const { user } = useAuth();
    const [showWaitlist, setShowWaitlist] = useState(false);
    return (
        <div className="min-h-screen bg-background grain-overlay">
            {showWaitlist && (
                <WaitlistModal tenant="trade" label="Join the Trade desk waitlist" onClose={() => setShowWaitlist(false)} />
            )}
            <header className="relative z-10 max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 py-6">
                <div className="flex items-baseline gap-3">
                    <EigooMark size="lg" sub="Trade" testid="trade-mark" />
                </div>
                <div className="flex items-center gap-3">
                    {user ? (
                        <Link to="/app/dashboard" className="px-5 py-2 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] text-sm font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors" data-testid="trade-enter-portal">Enter Portal</Link>
                    ) : (
                        <>
                            <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">Sign In</Link>
                            <BoardLink data-testid="trade-board-desk" className="px-5 py-2 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] text-sm font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors">Board the Desk</BoardLink>
                        </>
                    )}
                </div>
            </header>

            <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-8 pb-24">
                <div className="fade-up max-w-3xl">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[hsl(40_50%_40%/0.4)] text-[10px] uppercase tracking-[0.3em] text-[hsl(45_60%_70%)] mb-6">
                        <Radio size={10} className="text-[hsl(45_85%_62%)]" /> Live · Market Radar
                    </div>
                    <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
                        Liquidity signals,
                        <span className="block gold-gradient-text mt-2">read like tea leaves.</span>
                    </h1>
                    <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
                        The Trade desk reads order-book imbalance, cross-venue spreads, and dealer positioning —
                        then delivers only the signals worth staking capital on.
                    </p>
                </div>

                {/* Market radar */}
                <div className="mt-14 glass rounded-2xl p-6 fade-up" style={{ animationDelay: "0.15s" }}>
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <div className="font-heading text-2xl">Liquidity radar</div>
                            <div className="text-xs text-muted-foreground">Top conviction signals · refresh every 60s <span className="text-[hsl(45_85%_68%)]">(demo feed)</span></div>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-[hsl(45_85%_68%)]">
                            <span className="w-2 h-2 rounded-full bg-[hsl(45_85%_62%)] animate-pulse" />
                            signal feed active
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-[hsl(40_40%_30%/0.18)]">
                                <tr>
                                    <th className="py-3 pr-4">Symbol</th>
                                    <th className="py-3 pr-4">Bias</th>
                                    <th className="py-3 pr-4">Conviction</th>
                                    <th className="py-3 pr-4">Spread</th>
                                    <th className="py-3 pr-4">24h Vol</th>
                                    <th className="py-3 pr-4">Δ%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {LIQUIDITY_SIGNALS.map((s, i) => {
                                    const posColor = s.move > 0 ? "text-[hsl(153_50%_65%)]" : s.move < 0 ? "text-[hsl(350_75%_70%)]" : "text-muted-foreground";
                                    const biasPill = s.bias === "long" ? "bg-[hsl(153_40%_30%)] text-[hsl(153_50%_75%)]" : s.bias === "short" ? "bg-[hsl(350_55%_30%)] text-[hsl(350_75%_80%)]" : "bg-[hsl(151_55%_18%)] text-muted-foreground";
                                    return (
                                        <tr key={s.sym} className="border-b border-[hsl(40_40%_30%/0.08)]" data-testid={`signal-${i}`}>
                                            <td className="py-3 pr-4 font-mono">{s.sym}</td>
                                            <td className="py-3 pr-4"><span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${biasPill}`}>{s.bias}</span></td>
                                            <td className="py-3 pr-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-24 h-1.5 rounded-full bg-[hsl(151_55%_15%)] overflow-hidden"><div className="h-full bg-[hsl(40_75%_55%)]" style={{ width: `${s.conf * 100}%` }} /></div>
                                                    <span className="font-mono text-xs">{(s.conf * 100).toFixed(0)}%</span>
                                                </div>
                                            </td>
                                            <td className="py-3 pr-4 font-mono text-xs">{s.spread}</td>
                                            <td className="py-3 pr-4 font-mono text-xs">{s.vol}</td>
                                            <td className={`py-3 pr-4 font-mono text-xs ${posColor} inline-flex items-center gap-1`}>
                                                {s.move > 0 ? <TrendingUp size={12} /> : s.move < 0 ? <TrendingDown size={12} /> : <Activity size={12} />}
                                                {s.move > 0 ? "+" : ""}{s.move}%
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-6 p-4 rounded-lg border border-dashed border-[hsl(40_40%_30%/0.3)] text-xs text-muted-foreground">
                        <span className="text-[hsl(45_85%_68%)] font-semibold">Placeholder feed.</span> Wire your preferred data source (Alpha Vantage, CoinGecko, Polygon, IEX Cloud) and I'll replace this with live ticks in ~30 lines.
                    </div>
                </div>

                <div className="mt-10 flex flex-wrap gap-4">
                    <button onClick={() => setShowWaitlist(true)} className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors" data-testid="trade-cta">
                        Request access <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                    </button>
                    {user && (
                        <Link to="/app/dashboard" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-[hsl(40_50%_35%/0.4)] text-foreground hover:bg-[hsl(151_55%_15%)] transition-colors">
                            Open portal
                        </Link>
                    )}
                </div>

                {/* Sovereign Toolkit · Trade affiliate signals (Director directive 2026.02) */}
                <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="md:col-span-2">
                        <h2 className="font-heading text-2xl mb-2">High-margin signal log</h2>
                        <p className="text-sm text-muted-foreground max-w-xl leading-relaxed">
                            The Trade desk converts on Forex, CFD and crypto verticals. The Director's
                            scout has mapped the highest-yield programs to this platform. Each signal is a
                            live commission rail.
                        </p>
                    </div>
                    <SovereignToolkit vessel="eigoo_trade" title="Trade Toolkit" />
                </div>
            </section>

            <footer className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 border-t border-[hsl(40_40%_30%/0.15)] text-xs text-muted-foreground flex flex-wrap items-center justify-between gap-4">
                <div>© EIGOO Trade · Desk residuals under covenant.</div>
                <div className="font-mono">trade.eigooinc.net</div>
            </footer>
        </div>
    );
}
